import {
    BidPlannedRefuelingViewModel,
    BidPointViewModel,
    BidQueuedPlanRefuelingsViewModel,
    ItemChangePointListModel,
    MapObjectType,
    OrganizationDefaultRouteOptionsModel,
    RouteFullViewModel,
    RouteOptions as RouteOptionsModel,
    SegmentItemModel,
    SegmentItemPointModel,
    TollRoadSegmentModel,
    TrackerLogViewModel,
} from "api";
import { Checkbox, Flags } from "components/shared/form";
import Select from "components/shared/select";
import { hasFlag, normalizeAvoidTollRoadFlags, withDisabledAvoidTollRoadFlags } from "helpers/avoidTollRoadFlags";
import {
    avoidSpecialRoadFlags,
    avoidSpecialRoadFlagsEnum,
    avoidTollRoadFlags,
    avoidTollRoadFlagsEnum,
    mapObjectTypesEnum,
    regionTypesEnum,
    routerCountryTypes,
    routerProfiles,
    routerTypesEnum,
    routeTypesEnum,
} from "helpers/enums";
import {
    getClientAvoidSpecialRoadFlags,
    getClientRouteOptions,
    isRegionDryingPeriodsEnabledForClients,
    routeOptionsWithoutSeasonalRestrictions,
    seasonalRestrictionsFlagValue,
} from "helpers/regionDryingPeriods";
import { formatNumber, ymEvent } from "helpers/system";
import Tooltip from "rc-tooltip";
import React from "react";

interface RouteControlPoint {
    segmentId: number;
    name?: string;
    comment?: string;
    geozone?: BidPointViewModel["geozone"];
    location?: [number, number];
}

const { GraphHopper, Yatrs } = routerTypesEnum;

export const planLineStyle = { color: "#3a87e6", weight: 5 };
export const fixLineStyle = { color: "#ffb200", weight: 3 };
export const planDashLineStyle = { color: "#696969", weight: 5, dashArray: "10, 15", dashOffset: "5" };
export const factEmptyDashLineStyle = {
    color: "#249c66",
    weight: 5,
    dashArray: "10, 15",
    dashOffset: "5",
    arrowColor: "green-lighter",
};
export const actualMarkerStyle = {
    color: "#e26fc9",
    weight: 3,
    fill: true,
    fillColor: "#fff",
    fillOpacity: 1,
    radius: 4,
};
export const trackerMarkerStyle = { weight: 3, fill: true, fillColor: "#fff", fillOpacity: 1, radius: 4 };
export const trackerLineStyle = { weight: 4, opacity: 1, arrowColor: "blue" };
export const emulatedLineStyle = { color: "#c16200", weight: 4, opacity: 1, arrowColor: "green-lighter" };
export const emulatedEmptyDashLineStyle = {
    color: "#c16200",
    weight: 5,
    dashArray: "10, 15",
    dashOffset: "5",
    arrowColor: "green-lighter",
};
export const factMarkerStyle = {
    color: "#888888",
    weight: 3,
    fill: true,
    fillColor: "#fff",
    fillOpacity: 1,
    radius: 4,
};
export const mobileMarkerStyle = {
    color: "#4527a0",
    weight: 3,
    fill: true,
    fillColor: "#fff",
    fillOpacity: 1,
    radius: 4,
};
export const carStopMarkerStyle = {
    color: "#e5443c",
    weight: 3,
    fill: true,
    fillColor: "#fff",
    fillOpacity: 1,
    radius: 4,
};
export const totalRouteStyle = { color: "#3a87e6", weight: 6 };
export const suggestedRouteStyle = { color: "#888888", weight: 4, opacity: 0.6 };
export const taskMarkerStyle = {
    color: "#0351C1",
    weight: 2,
    fill: true,
    fillColor: "#fff",
    fillOpacity: 1,
    radius: 4,
};
export const taskJobRouteStyle = { bold: { color: "#0351C1", weight: 9 }, thin: { color: "#73c13e", weight: 3 } };
export const factLineStyle = {
    style: {
        weight: 4,
        opacity: 1,
        arrowColor: "green",
    },
    colors: [
        "#2dc581",
        "#00cc00",
        "#00cc66",
        "#00ff00",
        "#66ff66",
        "#2dc581",
        "#006600",
        "#669900",
        "#99cc00",
        "#339966",
    ],
};
export const actualLineStyle = {
    style: {
        weight: 4,
        opacity: 1,
        arrowColor: "pink",
    },
    colors: [
        "#e26fc9",
        "#cc66ff",
        "#cc33ff",
        "#ff00ff",
        "#cc0099",
        "#ff33cc",
        "#ff0066",
        "#990099",
        "#660066",
        "#9900cc",
    ],
};
export const mobileFactLineStyle = {
    style: {
        color: "#4527a0",
        weight: 4,
        opacity: 1,
        arrowColor: "purple",
    },
    colors: ["#4527a0"],
};

export const GEOZONE_FIELDS_BY_REGION_TYPES = {
    [regionTypesEnum.Country]: "countryId",
    [regionTypesEnum.FederalDistrict]: "federalDistrictId",
    [regionTypesEnum.FederalSubject]: "stateId",
    [regionTypesEnum.City]: "cityId",
    [regionTypesEnum.Town]: "townId",
    [regionTypesEnum.Village]: "villageId",
};

const routePointTimeStep = 15 * 1000 * 60;

function idGenerator() {
    let i = 0;
    return () => ++i;
}

export const id = idGenerator();

export function reduceLegCoords(
    acc: ConcatArray<[number, number]>[],
    leg: { geometry: { coordinates: [number, number][] } },
) {
    return acc.concat(
        leg.geometry.coordinates.map((item) => item.reverse()) as unknown as ConcatArray<[number, number]>,
    );
}

type SegmentPoint = {
    address?: string;
    id: number;
    radius?: number;
    type: keyof typeof MapObjectType;
    location: { coordinates: [number, number]; type: "Point" };
};

type SegmentPanelType = {
    panelType: "segment";
    distance: number;
    duration: number;
    i: number;
    geozone: { start: SegmentPoint; end: SegmentPoint };
};

type GasStationPanelType = BidPlannedRefuelingViewModel & {
    panelType: "gasstation";
    i: number;
    geozone: { start: SegmentPoint; end: SegmentPoint };
};

type BidPointPanelType = BidPointViewModel & {
    panelType: "bidpoint";
    i: number;
    geozone: SegmentItemPointModel & { type: "Point" };
};

type PanelType = SegmentPanelType | GasStationPanelType | BidPointPanelType;

export function bidPointsToMapData(
    points: BidPointViewModel[],
    segments: (SegmentItemModel & {
        points: [number, number][];
        distance: number;
        duration: number;
        id: number;
        routeType?: keyof typeof routeTypesEnum | undefined;
        roadRestrictions: {}[];
    })[],
    refueling: BidQueuedPlanRefuelingsViewModel,
    itemChangePoints: ItemChangePointListModel[] | undefined,
    tollRoadSegments: (TollRoadSegmentModel & { segmentIndex: number })[] | undefined,
    routeControlPoints?: RouteControlPoint[],
) {
    const segmentMapper: { [x: string]: number } = {};
    let _segments = (segments || []).map(
        ({ points, distance, duration, id: segmentId, routeType, roadRestrictions }) => {
            const segmentIndex = id();
            segmentMapper[segmentId] = segmentIndex;
            return {
                coords: points.map((el) => [el[1], el[0]]),
                routeInfo: { distance, duration },
                i: segmentIndex,
                routeType,
                roadRestrictions,
                roadControlPoints: [] as RouteControlPoint[],
            };
        },
    );
    const segmentByFrontendIndex: { [key: number]: (typeof _segments)[number] } = {};
    _segments.forEach((segment) => {
        segmentByFrontendIndex[segment.i] = segment;
    });
    tollRoadSegments?.forEach((element) => {
        element.segmentIndex = segmentMapper[element.segmentId];
    });
    routeControlPoints?.forEach((cp) => {
        const frontendI = segmentMapper[cp.segmentId];
        const segment = segmentByFrontendIndex[frontendI];
        if (segment) {
            segment.roadControlPoints.push(cp);
        }
    });
    const getBidPointInfo = ({ id }: SegmentItemPointModel) => points.find(({ geozone }) => geozone?.id === id);
    const getRefuelingInfo = ({ id }: SegmentItemPointModel) =>
        (refueling.plannedRefuelings || []).find((gasStation) => gasStation?.mapObject?.id === id);
    const getItemChangeInfo = ({ id }: SegmentItemPointModel) =>
        (itemChangePoints || []).find((item) => item.mapObjectId === id);
    let elements = [];
    if (segments && segments.length) {
        const bidStart = {
            panelType: "bidpoint",
            i: id(),
            geozone: {
                ...segments[0].mapObjectFrom,
                location: { coordinates: segments[0].mapObjectFrom!.location, type: "Point" },
            },
            ...getBidPointInfo(segments[0].mapObjectFrom!),
        };
        elements = segments.reduce(
            (prevValue, currValue, index) => {
                const { mapObjectFrom, mapObjectTo } = currValue;
                const start = { ...mapObjectFrom, location: { coordinates: mapObjectFrom?.location, type: "Point" } };
                const end = { ...mapObjectTo, location: { coordinates: mapObjectTo?.location, type: "Point" } };
                const geozone = { ...mapObjectTo, location: { coordinates: mapObjectTo?.location, type: "Point" } };
                const defaultPointParams = { i: id(), geozone };
                const point =
                    mapObjectTo!.type === mapObjectTypesEnum.BidPoint
                        ? { ...defaultPointParams, ...getBidPointInfo(mapObjectTo!), panelType: "bidpoint" }
                        : mapObjectTo!.type === mapObjectTypesEnum.ServicePoint
                          ? { ...defaultPointParams, ...getBidPointInfo(mapObjectTo!), panelType: "servicepoint" }
                          : mapObjectTo!.type === mapObjectTypesEnum.GasStation
                            ? { ...defaultPointParams, ...getRefuelingInfo(mapObjectTo!), panelType: "gasstation" }
                            : mapObjectTo!.type === mapObjectTypesEnum.ItemChangePoint
                              ? { ...defaultPointParams, ...getItemChangeInfo(mapObjectTo!), panelType: "itemchange" }
                              : { ...defaultPointParams, panelType: "geopoint" };
                const segment = { ..._segments[index], panelType: "segment", geozone: { start, end } };

                return [...prevValue, segment, point];
            },
            [bidStart],
        );
    } else {
        _segments = [];
        elements = points.reduce(
            (acc, point, i) => {
                acc.push({
                    panelType: "bidpoint",
                    i: id(),
                    ...point,
                } as BidPointPanelType);
                if (
                    points[i + 1] &&
                    point.geozone?.location?.coordinates &&
                    points[i + 1].geozone?.location?.coordinates
                ) {
                    const start: SegmentPoint = {
                        address: point.geozone.address,
                        id: point.geozone.id,
                        radius: point.geozone.radius,
                        type: point.geozone.type,
                        location: { coordinates: point.geozone.location.coordinates, type: "Point" },
                    };
                    const end: SegmentPoint = {
                        address: points[i + 1].geozone!.address,
                        id: points[i + 1].geozone!.id,
                        radius: points[i + 1].geozone!.radius,
                        type: points[i + 1].geozone!.type,
                        location: { coordinates: points[i + 1].geozone!.location.coordinates, type: "Point" },
                    };
                    const segmentIndex = id();
                    _segments.push({
                        coords: [[...start.location.coordinates].reverse(), [...end.location.coordinates].reverse()],
                        routeInfo: { distance: 0, duration: 0 },
                        i: segmentIndex,
                        routeType: undefined,
                        roadRestrictions: [],
                        roadControlPoints: [],
                    });
                    acc.push({
                        distance: 0,
                        duration: 0,
                        i: segmentIndex,
                        panelType: "segment",
                        geozone: { start, end },
                    });
                }
                return acc;
            },
            [] as Array<PanelType>,
        );
    }
    const notSegmentElements = elements.filter(({ panelType }) => panelType !== "segment");
    const markers = notSegmentElements.map((item, index) => {
        if (index === 0) {
            return { ...item, first: true, index };
        }
        if (index === notSegmentElements.length - 1) {
            return { ...item, last: true, index };
        }
        return { ...item, index };
    });
    return { segments: _segments, elements, markers, tollRoadSegments: tollRoadSegments || [] };
}

export function coordsToSegments(
    segments: (SegmentItemModel & {
        id: number;
        routeType?: keyof typeof routeTypesEnum | undefined;
        points: [number, number][];
    })[],
    tollRoadSegments: TollRoadSegmentModel[],
) {
    return segments.reduce(
        (memo, segment) => {
            const segmentPoints = segment.points.map((el) => [el[1], el[0]]);
            if (segment.routeType === routeTypesEnum.Main) {
                memo.totalRoute.push(segmentPoints);
            } else {
                memo.emptyMileageRoute.push(segmentPoints);
            }

            if (segment.mapObjectTo?.type === mapObjectTypesEnum.RouteSupportPoint) {
                memo.supportPoints.push([...(segment.mapObjectTo!.location as number[][])].reverse());
            }

            const tollRoads = tollRoadSegments?.filter((tollSegment) => tollSegment.segmentId === segment.id);
            if (tollRoads?.length > 0) {
                tollRoads.forEach((item) => {
                    memo.tollSegments.push({
                        ...item,
                        positions: segmentPoints.slice(item.startIndex, item.endIndex),
                    });
                });
            }

            return memo;
        },
        { totalRoute: [], supportPoints: [], emptyMileageRoute: [], tollSegments: [] } as unknown as {
            totalRoute: number[][][];
            supportPoints: number[][][];
            emptyMileageRoute: number[][][];
            tollSegments: (TollRoadSegmentModel & { positions: number[][] })[];
        },
    );
}

export function coordsToTimePoints(points: TrackerLogViewModel[]) {
    const trackerStops: { [x: string]: { location: any; fixedAt: string } } = {};
    const trackerCoords = points.map((point) => {
        const { fixedAt, location } = point;
        const loc = [location?.y, location?.x];
        const fixedTime = new Date(fixedAt);
        const roundedUp = Math.ceil(fixedTime.getTime() / routePointTimeStep) * routePointTimeStep;
        if (!trackerStops[roundedUp]) {
            trackerStops[roundedUp] = { location: loc, fixedAt };
        }
        return loc;
    });
    return { trackerCoords, trackerStops };
}

export function calcRouteDuration(params: number) {
    return (params / 3600).toFixed(1);
}

export function secondsToReadableTime(seconds: number) {
    if (!seconds) {
        return "-";
    }

    const secondsInHour = 60 * 60;
    const secondsInDay = 24 * secondsInHour;

    const days = Math.floor(seconds / secondsInDay);
    seconds = seconds - days * secondsInDay;

    const hours = Math.floor(seconds / secondsInHour);
    seconds = seconds - hours * secondsInHour;

    const minutes = Math.ceil(seconds / 60);

    return `${days ? `${days} д. ` : ""}${hours || days ? `${hours} ч. ` : ""}${`${minutes} мин.`}`;
}

export function calcRouteDistance(params: number) {
    return (params / 1000).toFixed(1);
}

export function metersToReadableDistance(meters: number, price: number) {
    if (!meters) {
        return "-";
    }

    const kms = Math.floor(meters / 1000);
    meters = Math.floor(meters - kms * 1000);

    return (
        <>
            Протяженность:&nbsp;
            <b>
                {formatNumber(kms)} км{kms > 5 ? "" : ` ${meters}м`}
            </b>
            {price > 0 && (
                <>
                    <br />
                    Общая стоимость: <b>{formatNumber(price, { style: "currency", currency: "RUB" })}</b> <br />
                </>
            )}
        </>
    );
}

export function matchSegmentToSequence(route: RouteFullViewModel) {
    const { segments, segmentSequences } = route;
    if (segments && segmentSequences) {
        route.segments = segments.sort(
            (a, b) =>
                segmentSequences.find((item) => item.segmentId === a.id)!.order -
                segmentSequences.find((item) => item.segmentId === b.id)!.order,
        );
    }

    return route;
}

export function clearGasStations(oldElements: { elements: PanelType[] }, activeSegmentId: number) {
    if (!oldElements.elements.some((item) => item.panelType === "gasstation" && !item.autoEnteredAt)) {
        return oldElements;
    }

    const res = oldElements.elements.reduce(
        (acc, item, index) => {
            if (
                (item.panelType === "gasstation" && !item.autoEnteredAt) || // точка заправки, которая не посещена
                (oldElements.elements[index - 1]?.panelType === "gasstation" && acc.gasStationRemoved) || // сегмент где первая точка это заправка и точка заправки, которая была до нее удалена
                (oldElements.elements[index + 1]?.panelType === "gasstation" &&
                    !(oldElements.elements[index + 1] as GasStationPanelType)?.autoEnteredAt) // сегмент где вторая точка это заправка и она не посещена
            ) {
                if (activeSegmentId && item.i === activeSegmentId) {
                    acc.nextSegmentIsActive = true;
                }
                acc.gasStationRemoved = true;
                return acc;
            }
            if (acc.gasStationRemoved) {
                acc.gasStationRemoved = false;
                const segment = {
                    coords: [],
                    routeInfo: { distance: 0 },
                    geozone: {
                        start: (acc.result.elements[acc.result.elements.length - 1] as GasStationPanelType).geozone,
                        end: item.geozone,
                    },
                    i: id(),
                    routeType: routeTypesEnum.Main,
                    panelType: "segment",
                    needCalculate: true,
                    roadRestrictions: [],
                };
                acc.result.elements.push(segment);
                acc.result.elements.push(item);

                acc.result.segments.push(segment);
                acc.result.markers.push(item);

                if (acc.nextSegmentIsActive) {
                    acc.nextSegmentIsActive = false;
                    acc.result.activeSegment = segment;
                }
            } else {
                acc.prevElement = item;
                acc.result.elements.push(item);
                if (item.panelType === "segment") {
                    acc.result.segments.push(item);
                } else {
                    acc.result.markers.push(item);
                }
            }
            return acc;
        },
        {
            result: { segments: [], markers: [], elements: [], activeSegment: undefined, tollRoadSegments: [] },
            prevElement: undefined,
            gasStationRemoved: false,
            nextSegmentIsActive: false,
        } as unknown as {
            result: {
                segments: {}[];
                markers: {}[];
                elements: {}[];
                activeSegment: {} | undefined;
                tollRoadSegments: {}[];
            };
            prevElement: PanelType | undefined;
            gasStationRemoved: boolean;
            nextSegmentIsActive: boolean;
        },
    );
    return res.result;
}

type BaseOptionConfig = {
    available: (routerType: keyof typeof routerTypesEnum) => boolean;
    label: string;
    inputProps: { name: string };
    key: string | undefined;
};

type CheckboxOptionConfig = BaseOptionConfig & { type: "checkbox" };
type FlagOptionConfig = BaseOptionConfig & {
    type: "flags";
    flags: (routerType: keyof typeof routerTypesEnum) => {
        label: string | React.ReactNode;
        value: string;
        intValue: number;
        disabled?: boolean;
    }[];
};
type SelectOptionConfig = BaseOptionConfig & {
    type: "select";
    options: { label: string; value: string }[];
    placeholder: string;
};

type OptionConfig = CheckboxOptionConfig | FlagOptionConfig | SelectOptionConfig;

const optionsConfig: OptionConfig[] = [
    {
        available: (routerType: keyof typeof routerTypesEnum) => Yatrs === routerType,
        label: "Профиль",
        key: "routerProfile",
        inputProps: { name: "routerProfile" },
        options: [],
        placeholder: "Профиль",
        type: "select",
    },
    {
        available: (routerType: keyof typeof routerTypesEnum) => Yatrs === routerType,
        label: "Использовать отдельный профиль для порожних маршрутов",
        key: "useSpecialProfileForEmptyRoute",
        inputProps: { name: "useSpecialProfileForEmptyRoute" },
        type: "checkbox",
    },
    {
        available: (routerType: keyof typeof routerTypesEnum) => [GraphHopper, Yatrs].includes(routerType),
        label: "Избегать все платные дороги",
        key: "disallowToll",
        inputProps: { name: "disallowToll" },
        type: "checkbox",
    },
    {
        available: (routerType: keyof typeof routerTypesEnum) => [GraphHopper, Yatrs].includes(routerType),
        label: "Избегать все платные дороги, кроме М4",
        key: "disallowTollExceptM4",
        inputProps: { name: "disallowTollExceptM4" },
        type: "checkbox",
    },
    {
        available: (routerType: keyof typeof routerTypesEnum) => [GraphHopper, Yatrs].includes(routerType),
        label: "Избегать все платные дороги, кроме участка \"Дюртюли - Ачит\" М-12",
        key: "disallowTollExceptM12DRT",
        inputProps: { name: "disallowTollExceptM12DRT" },
        type: "checkbox",
    },
    {
        available: (routerType: keyof typeof routerTypesEnum) => [GraphHopper, Yatrs].includes(routerType),
        label: "Исключать платные дороги",
        key: "avoidTollRoadFlags",
        inputProps: { name: "avoidTollRoadFlags" },
        flags: (routerType: keyof typeof routerTypesEnum) =>
            routerType === GraphHopper
                ? avoidTollRoadFlags.filter((item) => item.value === avoidTollRoadFlagsEnum.CRR)
                : avoidTollRoadFlags,
        type: "flags",
    },
    {
        available: (routerType: keyof typeof routerTypesEnum) => [GraphHopper, Yatrs].includes(routerType),
        label: "Исключать особые дороги",
        key: "avoidSpecialRoadFlags",
        inputProps: { name: "avoidSpecialRoadFlags" },
        flags: (routerType: keyof typeof routerTypesEnum) => {
            const flags =
                routerType === GraphHopper
                    ? avoidSpecialRoadFlags.filter((item) => item.value === avoidSpecialRoadFlagsEnum.MRR)
                    : avoidSpecialRoadFlags;
            return getClientAvoidSpecialRoadFlags(flags).map((item) =>
                item.value === avoidSpecialRoadFlagsEnum.Cond
                    ? {
                          ...item,
                          label: (
                              <>
                                  {item.label}
                                  <Tooltip
                                      overlayClassName="mw-350"
                                      overlay={
                                          <span onClick={(e) => e.stopPropagation()}>
                                              Отключение сезонных ограничений может проложить маршрут по дорогам с
                                              временным запретом движения. Перед использованием проверьте маршрут
                                              вручную.
                                          </span>
                                      }
                                      placement="top"
                                  >
                                      <i
                                          className="icon-uEA69-question-circle ml-1"
                                          onClick={(e) => e.stopPropagation()}
                                      />
                                  </Tooltip>
                              </>
                          ),
                      }
                    : item,
            );
        },
        type: "flags",
    },
    {
        available: (routerType: keyof typeof routerTypesEnum) => Yatrs === routerType,
        label: "Разрешить паромы",
        key: "allowFerry",
        inputProps: { name: "allowFerry" },
        type: "checkbox",
    },
    {
        available: (routerType: keyof typeof routerTypesEnum) => Yatrs === routerType,
        label: "Рассчитывать платные дороги",
        key: "calcTolls",
        inputProps: { name: "calcTolls" },
        type: "checkbox",
    },
    {
        available: (routerType: keyof typeof routerTypesEnum) => Yatrs === routerType,
        label: "Ограничение по стране",
        key: "country",
        inputProps: { name: "country" },
        options: routerCountryTypes,
        placeholder: "Ограничение по стране",
        type: "select",
    },
];

const condFlagIntValue = seasonalRestrictionsFlagValue;

export { getClientRouteOptions, routeOptionsWithoutSeasonalRestrictions };

export const seasonalRestrictionsWarningText = (
    <div className="text-left">
        <p>
            При отключении сезонных ограничений маршрут будет построен без учета временных запретов на движение
            грузового транспорта. Это может привести к проезду по запрещенным участкам и риску получения штрафов.
        </p>
        <p>Продолжая, вы подтверждаете, что:</p>
        <ul>
            <li>самостоятельно проверите маршрут перед рейсом;</li>
            <li>убедитесь в допустимости проезда по участкам маршрута;</li>
            <li>принимаете риск построения маршрута без учета сезонных ограничений.</li>
        </ul>
    </div>
);

export const seasonalRestrictionsConfirmParams = (action: () => unknown) => ({
    name: "confirm",
    title: "Внимание",
    mod: "full",
    windowMod: "sm modal-window--centered",
    text: seasonalRestrictionsWarningText,
    buttons: { confirm: "Подтвердить", cancel: "Отмена" },
    action,
});

export const RouteOptions = (props: {
    defaultRouteOptions: OrganizationDefaultRouteOptionsModel;
    routeOptions: RouteOptionsModel;
    refreshSegmentRoutes: () => void;
    updateSettings: (props: {}) => void;
    modal?: (params: {}) => void;
}) => {
    const { defaultRouteOptions, routeOptions, refreshSegmentRoutes, updateSettings, modal } = props;
    const routeOptionsForClients = getClientRouteOptions(routeOptions);
    const normalizedRouteOptions = {
        ...routeOptionsForClients,
        avoidTollRoadFlags: normalizeAvoidTollRoadFlags(routeOptionsForClients.avoidTollRoadFlags),
    };

    const applyChange = ({ name, value }: { name: string; value: any }) => {
        if (name === "useSpecialProfileForEmptyRoute" && value) {
            ymEvent("Hgv15tOptimalBidActivated");
        }
        updateSettings({
            routeOptions: {
                ...normalizedRouteOptions,
                [name]: name === "avoidTollRoadFlags" ? normalizeAvoidTollRoadFlags(value) : value,
            },
        });
        if (refreshSegmentRoutes) {
            setTimeout(refreshSegmentRoutes, 0);
        }
    };

    const onChange = ({ name, value }: { name: string; value: any }) => {
        const currentFlags = (normalizedRouteOptions.avoidSpecialRoadFlags as unknown as number) ?? 0;
        if (
            isRegionDryingPeriodsEnabledForClients &&
            modal &&
            name === "avoidSpecialRoadFlags" &&
            (value & condFlagIntValue) !== condFlagIntValue &&
            (currentFlags & condFlagIntValue) === condFlagIntValue
        ) {
            modal({
                ...seasonalRestrictionsConfirmParams(() => applyChange({ name, value })),
            });
            return;
        }
        applyChange({ name, value });
    };

    return optionsConfig.map((itemConfig) => {
        if (!itemConfig.available(defaultRouteOptions?.routerType)) {
            return null;
        }

        switch (itemConfig.type) {
            case "select": {
                const configProps: {
                    options: { label: string; value: string }[];
                    value?: string | number;
                    onChange?: (props: { name: string; value: any }) => void;
                    styles?: {};
                } = { ...itemConfig };
                if (itemConfig.inputProps.name === "routerProfile") {
                    configProps.options = routerProfiles;
                }
                configProps.value = normalizedRouteOptions[itemConfig.inputProps.name as keyof typeof RouteOptions];
                configProps.onChange = onChange;
                configProps.styles = {
                    placeholder: (styles: {}) => ({ ...styles, lineHeight: "18px", color: "#999", fontSize: "80%" }),
                };
                return (
                    <div className="mb-2" key={itemConfig.key || itemConfig.label}>
                        <Select {...configProps} />
                    </div>
                );
            }
            case "checkbox": {
                return (
                    <Checkbox
                        fieldBeforeLabel
                        labelWrapper="col-10 col-form-label col-form-label-sm pl-0"
                        fieldWrapper="col-auto"
                        wrapper="panel__row"
                        bWrapper="row mb-1 align-items-center"
                        onChange={onChange}
                        value={normalizedRouteOptions[itemConfig.inputProps.name as keyof typeof RouteOptions]}
                        {...itemConfig}
                        disabled={
                            (hasFlag(
                                normalizedRouteOptions.avoidTollRoadFlags,
                                avoidTollRoadFlagsEnum.M12WithoutDRT,
                            ) &&
                                !normalizedRouteOptions.disallowTollExceptM12DRT &&
                                itemConfig.inputProps.name === "disallowTollExceptM12DRT") ||
                            (normalizedRouteOptions.disallowTollExceptM12DRT &&
                                itemConfig.inputProps.name === "disallowToll") ||
                            (normalizedRouteOptions.disallowTollExceptM12DRT &&
                                itemConfig.inputProps.name === "disallowTollExceptM4") ||
                            (normalizedRouteOptions.disallowTollExceptM4 &&
                                itemConfig.inputProps.name === "disallowToll") ||
                            (normalizedRouteOptions.disallowTollExceptM4 &&
                                itemConfig.inputProps.name === "disallowTollExceptM12DRT") ||
                            (normalizedRouteOptions.disallowToll &&
                                itemConfig.inputProps.name === "disallowTollExceptM4") ||
                            (normalizedRouteOptions.disallowToll &&
                                itemConfig.inputProps.name === "disallowTollExceptM12DRT")
                        }
                    />
                );
            }
            case "flags": {
                const value = normalizedRouteOptions[itemConfig.inputProps.name as keyof typeof RouteOptions];
                const flags = itemConfig.flags(defaultRouteOptions?.routerType);

                return (
                    <div className="mb-2" key={itemConfig.key || itemConfig.label}>
                        <Flags
                            labelWrapper="col-form-label col-form-label-sm col-12 text-muted"
                            fieldWrapper="col-12"
                            bWrapper="row mb-2"
                            onChange={onChange}
                            value={value}
                            {...itemConfig}
                            flags={
                                itemConfig.inputProps.name === "avoidTollRoadFlags"
                                    ? withDisabledAvoidTollRoadFlags(flags, value as number)
                                    : flags
                            }
                            disabled={
                                (normalizedRouteOptions.disallowToll || 
                                    normalizedRouteOptions.disallowTollExceptM4 || 
                                    normalizedRouteOptions.disallowTollExceptM12DRT) &&
                                itemConfig.inputProps.name === "avoidTollRoadFlags"
                            }
                        />
                    </div>
                );
            }
            default: {
                return null;
            }
        }
    });
};

export const groupedCarRoutes = (
    data?:
        | {
              traveledRouteCoordinates: [number, number][];
              controlPoints: { routeType: keyof typeof routeTypesEnum; index: number }[];
              emulatedSegments: { index: number; startIndex: number; endIndex: number }[];
              startIndex: number;
              endIndex: number;
          }[]
        | undefined,
) =>
    data?.map(
        (item: {
            controlPoints: Array<{ routeType: keyof typeof routeTypesEnum; index: number }>;
            traveledRouteCoordinates: Array<[number, number]>;
            emulatedSegments: { startIndex: number; endIndex: number }[];
            startIndex: number;
            endIndex: number;
        }) => {
            const { controlPoints } = item;
            const traveledRouteCoordinates = item.traveledRouteCoordinates.map((p) => [p[1], p[0]]);

            const mainRouteMeta = controlPoints.find((item) => item.routeType === routeTypesEnum.Main);
            const emulatedSegments = (item?.emulatedSegments || []).reduce(
                (acc, segment) => {
                    if (mainRouteMeta && mainRouteMeta.index > item.startIndex && mainRouteMeta.index < item.endIndex) {
                        acc.push({
                            ...segment,
                            endIndex: mainRouteMeta.index,
                            coordinates: traveledRouteCoordinates.slice(segment.startIndex, mainRouteMeta.index),
                        });
                        acc.push({
                            ...segment,
                            startIndex: mainRouteMeta.index,
                            distance: 0,
                            coordinates: traveledRouteCoordinates.slice(mainRouteMeta.index, segment.endIndex),
                        });
                    } else {
                        acc.push({
                            ...segment,
                            coordinates: traveledRouteCoordinates.slice(segment.startIndex, segment.endIndex),
                            routeType:
                                mainRouteMeta && mainRouteMeta.index > segment.endIndex
                                    ? routeTypesEnum.Main
                                    : routeTypesEnum.EmptyMileage,
                        });
                    }
                    return acc;
                },
                [] as {
                    startIndex: number;
                    endIndex: number;
                    coordinates: number[][];
                    distance?: number;
                    routeType?: string;
                }[],
            );
            return {
                ...item,
                emulatedSegments,
                traveledRouteCoordinates: {
                    [routeTypesEnum.EmptyMileage]: traveledRouteCoordinates.slice(0, mainRouteMeta?.index),
                    [routeTypesEnum.Main]: mainRouteMeta
                        ? traveledRouteCoordinates.slice(mainRouteMeta?.index || 0)
                        : [],
                },
            };
        },
    );
